import { DragEvent, useRef, useState } from 'react';
import { FileSpreadsheet, Lock, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Props {
  analyzing: boolean;
  disabled?: boolean;
  onAnalyze: (file: File) => void;
}

const ACCEPTED = ['csv', 'xlsx', 'dat'];

export default function UploadPanel({ analyzing, disabled = false, onAnalyze }: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const blocked = analyzing || disabled;

  const choose = (file?: File) => {
    if (!file || blocked) return;
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!ACCEPTED.includes(extension)) {
      setError(t('reconciliation.upload.invalidType'));
      return;
    }
    setError('');
    onAnalyze(file);
  };

  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    choose(event.dataTransfer.files[0]);
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div
        role="button"
        tabIndex={blocked ? -1 : 0}
        aria-label={t('reconciliation.upload.choose')}
        aria-disabled={blocked}
        onClick={() => !blocked && inputRef.current?.click()}
        onKeyDown={(event) => {
          if (!blocked && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragEnter={(event) => { event.preventDefault(); if (!blocked) setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={drop}
        className={`flex min-h-44 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
          dragging ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-400'
        } ${blocked ? 'cursor-not-allowed opacity-70' : ''}`}
      >
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          accept=".csv,.xlsx,.dat"
          disabled={blocked}
          onChange={(event) => {
            choose(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        {analyzing ? (
          <>
            <span className="h-9 w-9 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
            <strong className="mt-4 text-gray-900">{t('reconciliation.upload.analyzing')}</strong>
            <span className="mt-1 text-sm text-gray-500">{t('reconciliation.upload.analyzingHelp')}</span>
          </>
        ) : (
          <>
            <span className="rounded-full bg-primary-50 p-3 text-primary-600">
              <Upload className="h-7 w-7" aria-hidden="true" />
            </span>
            <strong className="mt-3 text-gray-900">{t('reconciliation.upload.drop')}</strong>
            <span className="mt-1 text-sm text-primary-700">{t('reconciliation.upload.choose')}</span>
            <span className="mt-2 flex items-center gap-1 text-xs text-gray-500">
              <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
              {t('reconciliation.upload.formats')}
            </span>
          </>
        )}
      </div>
      <p className="mt-3 text-sm text-gray-600">{t('reconciliation.import.uploadHelp')}</p>
      {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}
      <p className="mt-3 flex items-start gap-2 text-xs text-gray-500">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        {t('reconciliation.upload.privacy')}
      </p>
    </section>
  );
}
